import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsForm from '../core/HeadersAndFooters/FooterButtonsForm';
import ConfirmationModal from '../core/ConfirmationModal';
import Modal from '../core/Modal';
import StandardButton from '../core/StandardButton';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import NotificationOptIn from '../core/NotificationOptIn';
// LocalityMapSelector removed - location management now in MyLocations only
import ImageCropper from '../core/ImageCropper/ImageCropper';
import ExportDataInfoModal from './ExportDataInfoModal';
import MapLocationPicker from '../locality/MapLocationPicker';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';

// Import Supabase clients from shared location
import { supabase, supabaseFetch } from '../../lib/supabase';
import { USER_DISPLAY_NAME_MAX, PASSWORD_MIN_LENGTH } from '../../utils/validationConstants';
import { authErrorMessage } from '../../utils/authFlow';
import { addCaughtError } from '../../utils/errorCapture';
import { createHomeAreaFromBoundary } from '../../utils/homeAreaSelfHealer';
import { resolveDefaultCountry } from '../../data/countriesService';
import { getAccountClosureSummary, requestAccountClosure, closureRefusal, formatClosureDeadline } from '../../data/accountService';
import { getCachedIpLocation } from '../../utils/ipGeolocation';

const MyProfile = ({ userId, onBack, currentContext, onLogout }) => {
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const { t, formattingLocale } = useTranslation('user');

  // Form state
  const [profileData, setProfileData] = useState({
    profile_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    profile_picture_url: ''
  });

  // Track initial state to detect changes
  const [initialProfileData, setInitialProfileData] = useState({
    profile_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    profile_picture_url: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Closure summary (#82): 'loading' | 'ready' | 'failed'; what will go, and groups that block it.
  const [closureSummary, setClosureSummary] = useState(null);
  const [closureSummaryState, setClosureSummaryState] = useState('loading');
  const [closureError, setClosureError] = useState(null);
  const [closureSummaryReload, setClosureSummaryReload] = useState(0);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [showPasswords, setShowPasswords] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportInfo, setShowExportInfo] = useState(false);

  // NEW - Image Cropper state
  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [profileImageDisplay, setProfileImageDisplay] = useState('');
  const [profileImageThumbnail, setProfileImageThumbnail] = useState('');

  // NEW - My Images modal state
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [userImages, setUserImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Home area state
  const [homeAreaName, setHomeAreaName] = useState(null);
  const [homeAreaAutoDetected, setHomeAreaAutoDetected] = useState(false);
  const [homeAreaId, setHomeAreaId] = useState(null);
  const [homePrimaryBoundaryId, setHomePrimaryBoundaryId] = useState(null);
  // 2026-09-02 country-selection foundation build: the existing home's own country, resolved
  // separately since user_areas has no country_code column of its own (DATABASE.md) — feeds
  // resolveDefaultCountry's top precedence tier so re-defining home opens on the right country
  // (success criterion 3) instead of the picker's own hardcoded/defaulted fallback.
  const [homeCountryCode, setHomeCountryCode] = useState(null);
  const [authUserId, setAuthUserId] = useState(null);
  const [showHomePicker, setShowHomePicker] = useState(false);
  const [homePickerSaving, setHomePickerSaving] = useState(false);
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Helper to validate UUID format (v4 and general UUIDs)
  const isValidUuid = (id) => {
    return typeof id === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);
  };

  // Get authenticated user ID from localStorage (supabase.auth.getUser() can hang) — gates the
  // home-change affordance to the signed-in user's own profile (this page can also be opened
  // read-only for another user via admin User Management; RLS on user_areas already restricts
  // the write to user_id = auth.uid(), but the affordance itself should not appear where it
  // could only ever silently no-op).
  useEffect(() => {
    try {
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (storedData?.user?.id) {
          setAuthUserId(storedData.user.id);
        }
      }
    } catch (err) {
      console.error('Error reading auth from localStorage:', err);
    }
  }, []);

  // Load user data on component mount
  const loadUserData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userId) {
        console.log('MyProfile: no userId - guest user, prompting login');
        setError(t('error_login_required'));
        setLoading(false);
        return;
      }

      if (!isValidUuid(userId)) {
        const msg = `Invalid user id format: "${userId}". Expected a UUID.`;
        console.error(msg);
        setError(msg);
        setLoading(false);
        return;
      }

      const queryId = userId;
      console.log('MyProfile: Loading profile for user:', queryId);

      // Use supabaseFetch for reliable queries (supabase client can hang)
      const { data, error: fetchError } = await supabaseFetch.query('users', {
        select: 'id,display_name,email,bio,avatar_url,created_at',
        eq: { id: queryId },
        single: true
      });

      console.log('MyProfile: Query result:', { data, fetchError });

      if (fetchError) {
        console.error('Profile fetch error:', fetchError);
        throw fetchError;
      }

      if (data) {
        const initialData = {
          profile_name: data.display_name || '',
          email: data.email || '',
          password: '', // Never populate password field from database
          confirmPassword: '',
          bio: data.bio || '',
          profile_picture_url: data.avatar_url || ''
        };

        setProfileData(initialData);
        setInitialProfileData(initialData);

        if (data.avatar_url) {
          setProfilePicturePreview(data.avatar_url);
        }

        // Load home area from user_areas
        const { data: homeArea, error: areaError } = await supabaseFetch.query('user_areas', {
          select: 'id,display_name,is_auto_detected,primary_boundary_id',
          eq: { user_id: queryId, is_home: true },
          single: true
        });

        if (areaError && areaError.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error loading home area:', areaError);
        } else if (homeArea) {
          setHomeAreaName(homeArea.display_name);
          setHomeAreaAutoDetected(homeArea.is_auto_detected || false);
          setHomeAreaId(homeArea.id);
          setHomePrimaryBoundaryId(homeArea.primary_boundary_id || null);
          // 2026-09-02 country-selection foundation build: NOT resolved here (Advisor post-diff
          // ADVISE — an extra get_boundaries_by_ids round-trip on every profile load, for every
          // user who has a home, only to serve the one button that opens the picker). Reset to
          // null so a previous home's country can't leak into this one; handleOpenHomePicker
          // resolves it lazily, only when the picker is actually about to open.
          setHomeCountryCode(null);
        } else {
          setHomeAreaId(null);
          setHomePrimaryBoundaryId(null);
          setHomeCountryCode(null);
        }
      }
    } catch (err) {
      // Log detailed error info so we can see status/message/details
      console.error('Error loading user data:', err, 'message:', err?.message, 'status:', err?.status, 'details:', err?.details || err?.hint || JSON.stringify(err));
      const userMessage = err?.message || (typeof err === 'string' ? err : t('error_load_failed'));
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Navigate to MyLocations for location management
  const handleManageLocations = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'locations' }
    }));
  };

  // 2026-09-01 home-picker-redefine-flow-fix-build: both branches now open the same simple
  // single-select picker (previously an existing-home user got ChangeHomeAreaStep's full
  // multi-select crosshair, now retired — see handleHomeBoundarySelected for where the two
  // branches actually diverge, on write, not on surface).
  const handleOpenHomePicker = async () => {
    setError(null);
    setSuccessMessage(null);
    // 2026-09-02 country-selection foundation build: resolved here, not in loadUserData — this
    // button is the only consumer of homeCountryCode, so the extra get_boundaries_by_ids
    // round-trip only happens when it's actually about to be used, not on every profile load.
    // Must complete before the picker mounts (its countryCode prop only seeds framing once, at
    // mount) — an existing-home user re-defining home while travelling is exactly the case
    // success criterion 3 exists to cover, so this can't be a fire-and-forget background update.
    if (homePrimaryBoundaryId && homeCountryCode === null) {
      try {
        const { data: boundaries } = await supabaseFetch.rpc('get_boundaries_by_ids', {
          p_ids: [homePrimaryBoundaryId]
        });
        setHomeCountryCode(boundaries?.[0]?.country_code || null);
      } catch (err) {
        console.error('Error resolving home country:', err);
      }
    }
    setShowHomePicker(true);
  };

  // 2026-09-01 home-picker-redefine-flow-fix-build: existing-home selection no longer writes
  // here at all — it hands the picked core off to My Locations (a "set-home session"), which
  // creates the new area (pre-filled identity, auto-filled first-tier adjacency) and grants it
  // home on Save-and-Exit. This also closes the same "stale cached homeAreaId -> silent 0-row
  // UPDATE" risk the prior ChangeHomeAreaStep-based repoint closed
  // (docs/briefs/2026-08-26-change-home-create-new-model-probe-report.md, item 2) — there is no
  // client-held area id in this path at all now; My Locations resolves the user's current home
  // itself, server-side, at Save-and-Exit.
  const handleHomeBoundarySelected = async (selection) => {
    setShowHomePicker(false);
    if (!selection?.boundaryId || !userId) return;

    if (homeAreaId) {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'locations', seedBoundaryId: selection.boundaryId }
      }));
      return;
    }

    setHomePickerSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Belt-and-braces: never persist a superseded boundary as home, even though the ladder's
      // own fetch (get_boundaries_by_level) is already superseded-filtered.
      const { data: statusRow } = await supabaseFetch.query('admin_boundaries', {
        select: 'superseded_by',
        eq: { id: selection.boundaryId },
        single: true
      });
      if (statusRow?.superseded_by) {
        setError(t('home_picker_superseded_error'));
        return;
      }

      const result = await createHomeAreaFromBoundary(userId, selection.boundaryId, { isAutoDetected: false });
      // 2026-08-29 create/heal-under-latency fix (Fault B): 'home-now-exists' is
      // createHomeAreaFromBoundary's own last-moment re-check finding a home was created
      // concurrently (the exact race this fix closes) — the user's actual goal (having a home)
      // is already satisfied, so this is a correct stand-down, not a failure. Treated the same as
      // result.created rather than thrown into the generic error path below, which would
      // otherwise show "something went wrong" for a user whose home now genuinely exists (just
      // not necessarily the one they picked in this dialog — loadUserData() below picks up
      // whichever one actually won).
      if (!result.created && result.reason !== 'home-now-exists') throw new Error(result.reason || 'create-failed');

      window.dispatchEvent(new CustomEvent('ournet:refresh-locations', { detail: { userId } }));
      await loadUserData();
      setSuccessMessage(t('home_update_success'));
    } catch (err) {
      console.error('Error setting home area:', err);
      // 2026-08-29 area-code collision fix, extended (docs/briefs/2026-08-29-area-code-collision-all-home-paths.md):
      // createHomeAreaFromBoundary now retries internally on a code collision — this reason only
      // surfaces if that bounded retry is itself exhausted (astronomically unlikely). Named
      // specifically so a real remedy ("try again") isn't buried in the generic message.
      setError(err.message === 'code-collision-exhausted' ? t('home_update_error_code_collision') : t('home_update_error'));
    } finally {
      setHomePickerSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccessMessage(null);
  };

  // NEW - Profile image save handler (from ImageCropper)
  const handleProfileImageSave = ({ displayUrl, thumbnailUrl }) => {
    setProfileImageDisplay(displayUrl);
    setProfileImageThumbnail(thumbnailUrl);
    setProfilePicturePreview(displayUrl);
    setProfileData(prev => ({ ...prev, profile_picture_url: displayUrl }));
  };

  // NEW - Load user's images from Supabase storage
  const loadUserImages = async () => {
    setLoadingImages(true);
    try {
      // Get all images from hero-images bucket for this user
      const { data: heroImages, error: heroError } = await supabase.storage
        .from('hero-images')
        .list(`public/${userId}`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      // Get all images from inline-images bucket for this user
      const { data: inlineImages, error: inlineError } = await supabase.storage
        .from('inline-images')
        .list(`public/${userId}`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (heroError) console.error('Error loading hero images:', heroError);
      if (inlineError) console.error('Error loading inline images:', inlineError);

      // Combine and format images
      const allImages = [];

      if (heroImages) {
        heroImages.forEach(img => {
          if (img.name && !img.name.includes('_thumb')) {
            const { data: urlData } = supabase.storage
              .from('hero-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            allImages.push({
              id: img.id,
              name: img.name,
              url: urlData.publicUrl,
              bucket: 'hero-images',
              size: img.metadata?.size,
              created_at: img.created_at
            });
          }
        });
      }

      if (inlineImages) {
        inlineImages.forEach(img => {
          if (img.name && !img.name.includes('_thumb')) {
            const { data: urlData } = supabase.storage
              .from('inline-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            allImages.push({
              id: img.id,
              name: img.name,
              url: urlData.publicUrl,
              bucket: 'inline-images',
              size: img.metadata?.size,
              created_at: img.created_at
            });
          }
        });
      }

      setUserImages(allImages);
    } catch (error) {
      console.error('Error loading user images:', error);
      setModal({
        visible: true,
        message: t('error_images_load_failed'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setLoadingImages(false);
    }
  };

  // NEW - Delete user image
  const handleDeleteImage = async (image) => {
    try {
      const { error } = await supabase.storage
        .from(image.bucket)
        .remove([`public/${userId}/${image.name}`]);

      if (error) throw error;

      // Also try to delete thumbnail
      const thumbName = image.name.replace(/(\.[^.]+)$/, '_thumb$1');
      await supabase.storage
        .from(image.bucket)
        .remove([`public/${userId}/${thumbName}`]);

      // Reload images
      loadUserImages();

      setModal({
        visible: true,
        message: t('success_image_deleted'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      setModal({
        visible: true,
        message: t('error_image_delete_failed'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  const validateForm = () => {
    if (!profileData.profile_name.trim()) {
      setModal({
        visible: true,
        message: t('error_name_required'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return false;
    }

    if (!profileData.email.trim()) {
      setModal({
        visible: true,
        message: t('error_email_required'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      setModal({
        visible: true,
        message: t('error_email_invalid'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return false;
    }


    // If password is being changed, validate it
    if (profileData.password) {
      // The one shared password minimum (registration-confirm-first addendum 2 item 1).
      if (profileData.password.length < PASSWORD_MIN_LENGTH) {
        setModal({
          visible: true,
          message: t('auth:password_min_length', { min: PASSWORD_MIN_LENGTH }),
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
          }
        });
        return false;
      }

      if (profileData.password !== profileData.confirmPassword) {
        setModal({
          visible: true,
          message: t('error_passwords_mismatch'),
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
          }
        });
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (!userId) {
        const msg = 'Cannot save profile: missing userId.';
        console.error(msg);
        setError(msg);
        return;
      }


      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // The picture is set by the image cropper (handleProfileImageSave), already uploaded.
      const profilePictureUrl = profileData.profile_picture_url;

      // Prepare update data (excluding password - handled separately via Supabase Auth)
      const updateData = {
        display_name: profileData.profile_name,
        email: profileData.email,
        bio: profileData.bio,
        avatar_url: profilePictureUrl
        // Note: home_locality_id removed - location management now in MyLocations (user_areas table)
      };

      // If password is being changed, handle authentication update via Supabase Auth
      if (profileData.password) {
        // Get session user ID from localStorage (avoid auth.getUser which can hang)
        const authToken = await supabaseFetch.getAuthToken();
        let sessionUserId = null;
        if (authToken) {
          try {
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            sessionUserId = payload.sub;
          } catch (e) {
            console.error('Error decoding auth token:', e);
          }
        }

        // A password failure is shown translated from Supabase's error code, raw text to the error
        // log only (addendum 2 item 1); the users-table update below keeps its own handling.
        const showPasswordError = (err) => {
          const mapped = authErrorMessage(err, 'password');
          if (!mapped.known) addCaughtError(err?.message || String(err), 'MyProfile:password');
          setModal({
            visible: true,
            message: t(`auth:${mapped.key}`),
            onOk: () => {
              setModal({ visible: false, message: '', onOk: null });
            }
          });
        };

        if (!sessionUserId || sessionUserId !== userId) {
          console.error('Password change: session does not match this profile', { sessionUserId, targetUserId: userId });
          showPasswordError({ code: 'session_mismatch' });
          return;
        }

        // Update password via Supabase Auth (NOT in users table)
        // Note: This auth call is required and can't be avoided
        let authError = null;
        try {
          ({ error: authError } = await supabase.auth.updateUser({
            password: profileData.password
          }));
        } catch (err) {
          authError = err;
        }

        if (authError) {
          showPasswordError(authError);
          return;
        }
      }

      // Update user record
      // Get auth token from localStorage for RLS
      let authToken = null;
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        try {
          const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}');
          authToken = storedData?.access_token;
        } catch (e) {
          console.warn('Failed to parse auth token from localStorage');
        }
      }

      const { error: updateError } = await supabaseFetch.update('users', updateData, {
        eq: { id: userId },
        authToken
      });

      if (updateError) {
        // If RLS triggers, surface a clear message
        console.error('Supabase update error:', updateError);
        if (updateError?.message && updateError.message.toLowerCase().includes('row-level')) {
          throw new Error('Update blocked by database row-level security policy. Ensure the signed-in user matches the profile being edited or perform this update from a trusted server.');
        }
        throw updateError;
      }

      markSaved();
      setModal({
        visible: true,
        message: t('success_updated'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (onBack) {
            onBack();
          }
        }
      });

      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

    } catch (err) {
      console.error('Error saving profile:', err);
      const rawMessage = err?.message || (typeof err === 'string' ? err : JSON.stringify(err));
      const message = rawMessage || 'Failed to save profile. Please try again.';
      setModal({
        visible: true,
        message,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setSaving(false);
    }
  };

  // Closure summary: read each time the dialog opens, so it is exactly what would go now.
  useEffect(() => {
    if (!showDeleteConfirm) return;
    let cancelled = false;
    setClosureSummary(null);
    setClosureSummaryState('loading');
    getAccountClosureSummary().then(({ data, error: summaryError }) => {
      if (cancelled) return;
      if (summaryError || !data) {
        console.error('Error loading closure summary:', summaryError);
        setClosureSummaryState('failed');
        return;
      }
      setClosureSummary(data);
      setClosureSummaryState('ready');
    });
    return () => { cancelled = true; };
  }, [showDeleteConfirm, closureSummaryReload]);

  const closureBlocked = (closureSummary?.blocking_groups?.length || 0) > 0;

  const handleDeleteAccount = async () => {
    setSaving(true);
    setClosureError(null);

    // request_account_closure() (migration 20260928g) hides everything the summary lists at once and
    // starts the 30-day way back; the deletion runs later on the scheduler. Or it refuses and
    // changes nothing.
    const { error: closeError } = await requestAccountClosure();
    if (closeError) {
      console.error('Error closing account:', closeError);
      const refusal = closureRefusal(closeError);
      setClosureError(refusal.reason === 'owned_groups'
        ? t('closure_owned_groups_block', { groups: refusal.groups })
        : t('error_delete_account_failed'));
      setSaving(false);
      // Re-read the summary so the dialog shows what now blocks closing.
      setClosureSummaryReload(n => n + 1);
      return;
    }

    // The account is closing. Sign out everywhere (the default scope revokes every device's
    // session). A failed sign-out must not be reported as a failed closure: it has happened.
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.warn('Sign-out after closing the account failed (the account is closing):', signOutError);
    }
    setShowDeleteConfirm(false);
    if (onLogout) onLogout();
  };

  const handleExportData = async () => {
    setExporting(true);
    setError(null);

    try {
      // Get auth token from localStorage (avoid auth.getSession which can hang)
      const authToken = await supabaseFetch.getAuthToken();

      if (!authToken) {
        setError(t('error_export_auth_required'));
        return;
      }

      // Call the export edge function
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/export-user-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      // Download the JSON file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ournet-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccessMessage(t('success_data_exported'));
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message || t('error_export_failed'));
    } finally {
      setExporting(false);
    }
  };

  // Signing out does not navigate afterwards: App's clean start (resetForIdentityChange, Roadmap
  // #100) decides where a sign-out lands — the locality feed. Calling onBack here would restore
  // App's previousContext as captured before the reset ('groups' when Profile was opened from a
  // group) and land on Group Discovery (addendum 2).
  const handleLogout = async () => {
    try {
      // Sign out from Supabase Auth
      await supabase.auth.signOut();

      // Call parent logout handler to clear app state
      if (onLogout) {
        onLogout();
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Even if there's an error, still log out (App's handleLogout retries and resets)
      if (onLogout) {
        onLogout();
      }
    }
  };

  // Helper to detect if there are unsaved changes
  const hasUnsavedChanges = () => {
    // Check if any form fields have changed
    const fieldsChanged =
      profileData.profile_name !== initialProfileData.profile_name ||
      profileData.email !== initialProfileData.email ||
      profileData.bio !== initialProfileData.bio ||
      profileData.password !== '' ||
      profileData.confirmPassword !== '' ||
      profileData.profile_picture_url !== initialProfileData.profile_picture_url;

    // A picture chosen in the image cropper and not yet saved
    const pictureChanged = profileImageDisplay !== '';

    return fieldsChanged || pictureChanged;
  };

  // #78: this form already compares with what was loaded (hasUnsavedChanges); the shared hook
  // adds the tab-close and browser-Back questions and the one "leave without saving" dialog.
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    dirty: hasUnsavedChanges(),
    onLeave: () => { if (onBack) onBack(); },
  });

  // Cancel (and the header Back) asks only when something changed.
  const handleCancel = async () => {
    if ((await confirmLeave()) && onBack) onBack();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">{t('profile_loading')}</div>
      </div>
    );
  }

  return (
    //<div className="min-h-screen bg-gray-50 py-8">
    //  {/* Header */}
    //  <div className="max-w-4xl mx-auto px-4 mb-8">
    //    <div className="flex items-center justify-between">
    //      <div className="flex items-center gap-4">
    //        <img 
    //          src="/images/logos/header-logo.png" 
    //          alt="OurNet" 
    //          className="h-10"
    //        />
    //      </div>
    //      <button
    //        onClick={onBack}
    //        className="px-6 py-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow flex items-center gap-2"
    //      >
    //        <img src="/images/icons/icon-home.png" alt="Home" className="h-5 w-5" />
    //        <span>Home</span>
    //      </button>
    //    </div>
    //  </div>

      <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header - Layered architecture (siblings, not wrapper) */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={currentContext}
        headerTitle={t('profile_header_title')}
        onBack={handleCancel}
      />

      {/* Main Content */}
      <div style={{
        marginTop: '80px',
        marginBottom: isMobile ? '80px' : '120px',
        paddingTop: '25px',
        paddingBottom: '25px',
        position: 'relative',
        zIndex: 1
      }}>
        <div className="max-w-[1600px]" style={{ marginLeft: isMobile ? '16px' : '40px', marginRight: isMobile ? '16px' : '0px' }}>
          <div className={isMobile ? 'flex flex-col gap-6' : 'flex gap-8'}>

            {/* Left Column - Map Selector (Fixed 480px on desktop, full width on mobile - shows SECOND on mobile) */}
            <div className="space-y-3" style={isMobile ? { width: '100%', order: 2 } : { width: '480px', flexShrink: 0 }}>
              {/* Locality Map Selector */}
              <div>
                <div style={{
                  background: theme.cardBg || 'white',
                  border: '5px solid white',
                  borderRadius: '8px',
                  padding: isMobile ? '16px' : '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  width: isMobile ? '100%' : '448px',
                  height: isMobile ? '500px' : '720px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <h2 style={{
                    margin: '0 0 16px 0',
                    fontSize: '18px',
                    fontFamily: 'OpenSauceSans',
                    fontWeight: '600',
                    color: theme.text?.primary || '#1e293b'
                  }}>
                    {t('home_location_title')}
                  </h2>
                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: '14px',
                    color: theme.text?.secondary || '#64748b'
                  }}>
                    {homeAreaName
                      ? (homeAreaAutoDetected
                          ? t('home_area_is_default', { area: homeAreaName })
                          : t('home_area_is', { area: homeAreaName }))
                      : t('no_home_location')}
                  </p>
                  {homeAreaAutoDetected && homeAreaName && (
                    <p style={{
                      margin: '0 0 20px 0',
                      fontSize: '13px',
                      color: theme.text?.secondary || '#64748b'
                    }}>
                      {t('home_area_default_hint')}
                    </p>
                  )}
                  {!homeAreaAutoDetected && <div style={{ marginBottom: '12px' }} />}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {!!userId && userId === authUserId && (
                      <button
                        onClick={handleOpenHomePicker}
                        disabled={homePickerSaving}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: 'transparent',
                          color: theme.primary?.main || '#024B99',
                          border: `1px solid ${theme.primary?.main || '#024B99'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: homePickerSaving ? 'default' : 'pointer',
                          opacity: homePickerSaving ? 0.6 : 1,
                          fontFamily: 'OpenSauceSans'
                        }}
                      >
                        {homeAreaName ? t('home_change_button') : t('home_set_button')}
                      </button>
                    )}
                    <button
                      onClick={handleManageLocations}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: theme.primary?.main || '#024B99',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontFamily: 'OpenSauceSans'
                      }}
                    >
                      {t('manage_locations')}
                    </button>
                  </div>
                  {!homeAreaName && (
                    <p style={{
                      margin: '16px 0 0 0',
                      fontSize: '12px',
                      color: theme.text?.secondary || '#64748b',
                      fontStyle: 'italic'
                    }}>
                      {t('manage_locations_hint')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Profile Content (Flexible) - shows FIRST on mobile */}
            <div className="flex-1" style={isMobile ? { order: 1 } : {}}>
              <div className={isMobile ? 'flex flex-col gap-6' : 'grid grid-cols-1 lg:grid-cols-3 gap-8'}>

                {/* Profile Picture Column */}
                <div className={isMobile ? '' : 'lg:col-span-1'}>
                  <div className="bg-white p-6 shadow-sm" style={{ borderRadius: '8px', height: isMobile ? 'auto' : '720px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <h2 className={isMobile ? 'text-lg font-bold mb-4' : 'text-2xl font-bold mb-6'}>{t('profile_picture_title')}</h2>

              {/* Mobile: Side-by-side layout for profile picture and buttons */}
              {isMobile ? (
                <div style={{ display: 'flex', gap: '16px' }}>
                  {/* Left: Profile Picture (half width) */}
                  <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-lg">
                        {profilePicturePreview ? (
                          <img
                            src={profilePicturePreview}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-3xl">
                            👤
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Button */}
                    <button
                      type="button"
                      onClick={() => setShowProfileCropper(true)}
                      className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors text-sm"
                    >
                      {profilePicturePreview ? t('picture_button_change') : t('picture_button_upload')}
                    </button>

                    {/* Remove Photo Button */}
                    {profilePicturePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setProfilePicturePreview(null);
                          setProfileImageDisplay('');
                          setProfileImageThumbnail('');
                          setProfileData(prev => ({ ...prev, profile_picture_url: '' }));
                        }}
                        className="mt-1 px-3 py-1 text-xs text-red-600 hover:text-red-700"
                      >
                        {t('picture_button_remove')}
                      </button>
                    )}
                  </div>

                  {/* Right: Action Buttons (half width) */}
                  <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Export Data Button */}
                    <button
                      onClick={handleExportData}
                      disabled={exporting}
                      className="w-full py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {exporting ? t('export_button_loading') : t('export_button_default')}
                    </button>
                    <button
                      onClick={() => setShowExportInfo(true)}
                      className="text-xs text-blue-600 hover:underline block mx-auto"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {t('export_whats_included')}
                    </button>

                    {/* Terms & Conditions Links */}
                    <div className="text-center mt-1">
                      <p className="text-xs font-bold text-gray-700 mb-1">{t('terms_heading')}</p>
                      <a href="/tac/TERMS.md" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">{t('terms_link')}</a>
                      <a href="/tac/PRIVACY.md" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">{t('privacy_link')}</a>
                      <a href="/tac/LICENSE.md" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">{t('license_link')}</a>
                    </div>

                    {/* Notifications opt-in — deliberate, per device (Commit 2) */}
                    <NotificationOptIn userId={userId} />

                    {/* Log Out Button */}
                    <button
                      onClick={handleLogout}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm"
                    >
                      {t('button_logout')}
                    </button>

                    {/* Delete Account Button */}
                    <button
                      onClick={() => { setClosureError(null); setShowDeleteConfirm(true); }}
                      className="w-full py-2 bg-white text-red-600 border-2 border-red-600 rounded-lg font-semibold hover:bg-red-50 transition-colors text-sm"
                    >
                      {t('button_delete_account')}
                    </button>
                  </div>
                </div>
              ) : (
                /* Desktop: Original vertical layout */
                <>
                  {/* Profile Picture */}
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div className="w-48 h-48 rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-lg">
                        {profilePicturePreview ? (
                          <img
                            src={profilePicturePreview}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                            👤
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Button - Using ImageCropper */}
                    <button
                      type="button"
                      onClick={() => setShowProfileCropper(true)}
                      className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
                    >
                      {profilePicturePreview ? t('picture_button_change_photo') : t('picture_button_upload_photo')}
                    </button>

                    {/* Remove Photo Button */}
                    {profilePicturePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setProfilePicturePreview(null);
                          setProfileImageDisplay('');
                          setProfileImageThumbnail('');
                          setProfileData(prev => ({ ...prev, profile_picture_url: '' }));
                        }}
                        className="mt-2 px-4 py-1 text-sm text-red-600 hover:text-red-700"
                      >
                        {t('picture_button_remove_photo')}
                      </button>
                    )}
                  </div>

                  {/* Export Data Button */}
                  <div className="mt-6">
                    <button
                      onClick={handleExportData}
                      disabled={exporting}
                      className="w-full py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {exporting ? t('export_button_loading') : t('export_button_default_desktop')}
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {t('export_description')}
                    </p>
                    <button
                      onClick={() => setShowExportInfo(true)}
                      className="text-xs text-blue-600 hover:underline mt-1 block mx-auto"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {t('export_whats_included')}
                    </button>
                  </div>

                  {/* Terms & Conditions Links */}
                  <div className="mt-4 text-center">
                    <p className="text-sm font-bold text-gray-700 mb-1">{t('terms_heading')}</p>
                    <a href="/tac/TERMS.md" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">{t('terms_link')}</a>
                    <a href="/tac/PRIVACY.md" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">{t('privacy_link')}</a>
                    <a href="/tac/LICENSE.md" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">{t('license_link')}</a>
                  </div>

                  {/* Notifications opt-in — deliberate, per device (Commit 2) */}
                  <div className="mt-4">
                    <NotificationOptIn userId={userId} />
                  </div>

                  {/* Log Out Button */}
                  <div className="mt-4">
                    <button
                      onClick={handleLogout}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      {t('button_logout')}
                    </button>
                  </div>

                  {/* Delete Account Button */}
                  <div className="mt-4">
                    <button
                      onClick={() => { setClosureError(null); setShowDeleteConfirm(true); }}
                      className="w-full py-3 bg-white text-red-600 border-2 border-red-600 rounded-lg font-semibold hover:bg-red-50 transition-colors"
                    >
                      {t('button_delete_account')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

                {/* Form Fields Column */}
                <div className={isMobile ? '' : 'lg:col-span-2'}>
                  <div className="bg-white p-6 shadow-sm" style={{ borderRadius: '8px', height: isMobile ? 'auto' : '720px', overflowY: 'auto' }}>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                  {error}
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
                  {successMessage}
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                
                {/* Profile Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form_name_label')}
                  </label>
                  <input
                    type="text"
                    name="profile_name"
                    value={profileData.profile_name}
                    onChange={handleInputChange}
                    placeholder={t('form_name_placeholder')}
                    maxLength={USER_DISPLAY_NAME_MAX}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form_email_label')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleInputChange}
                    placeholder={t('form_email_placeholder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form_bio_label')}
                  </label>
                  <textarea
                    name="bio"
                    value={profileData.bio}
                    onChange={handleInputChange}
                    placeholder={t('form_bio_placeholder')}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form_password_label')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      name="password"
                      value={profileData.password}
                      aria-describedby={profileData.password && profileData.password.length < PASSWORD_MIN_LENGTH ? 'profile-password-hint' : undefined}
                      onChange={handleInputChange}
                      placeholder={t('form_password_placeholder')}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPasswords(prev => !prev);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer z-10 bg-transparent border-none p-1"
                      style={{ fontSize: '18px' }}
                      title={showPasswords ? t('form_password_hide_title') : t('form_password_show_title')}
                    >
                      {showPasswords ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('form_password_hint')}
                  </p>
                  {/* As the person types: the shared minimum (addendum 2 item 1). */}
                  {profileData.password && profileData.password.length < PASSWORD_MIN_LENGTH && (
                    <p id="profile-password-hint" className="text-sm mt-1" style={{ color: '#6B7280' }}>
                      {t('auth:password_min_length', { min: PASSWORD_MIN_LENGTH })}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form_confirm_password_label')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      name="confirmPassword"
                      value={profileData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder={t('form_confirm_password_placeholder')}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPasswords(prev => !prev);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer z-10 bg-transparent border-none p-1"
                      style={{ fontSize: '18px' }}
                      title={showPasswords ? t('form_password_hide_title') : t('form_password_show_title')}
                    >
                      {showPasswords ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

              </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Data Info Modal */}
      <ExportDataInfoModal
        visible={showExportInfo}
        onClose={() => setShowExportInfo(false)}
      />

      {/* Home picker — simple single-select for both branches (2026-09-01 home-picker-redefine-
          flow-fix-build). No-home: unchanged direct create. Existing-home: hands off to My
          Locations instead of writing here — see handleHomeBoundarySelected.
          2026-09-02 country-selection foundation build: countryCode was a hardcoded "PT" literal
          (the entire mechanism behind the "stuck in Portugal" family for this surface, per
          docs/briefs/2026-09-02-map-picker-country-affordance-probe-RESULTS.md) — now the
          existing-home → IP → configured-fallback chain, same for both branches (existing-home
          resolves via homeCountryCode when present; no-home falls through to IP/fallback). */}
      {showHomePicker && (
        <MapLocationPicker
          mode="change"
          onSelect={handleHomeBoundarySelected}
          onClose={() => setShowHomePicker(false)}
          detectedBoundaryId={homePrimaryBoundaryId}
          countryCode={resolveDefaultCountry({
            existingHomeCountryCode: homeCountryCode,
            ipCountryCode: getCachedIpLocation()?.countryCode
          })}
        />
      )}

      {/* Delete Confirmation Modal — what will go, then the confirmation (#82) */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => { if (!saving) setShowDeleteConfirm(false); }}
        closeOnBackdrop={!saving}
        closeOnEsc={!saving}
        panelClassName="bg-white rounded-lg p-8 max-w-md mx-4"
      >
        <h3 className="text-xl font-bold mb-4">{t('delete_modal_title')}</h3>
        {closureSummaryState === 'loading' && (
          <p role="status" className="text-gray-600 mb-4">{t('closure_summary_loading')}</p>
        )}
        {closureSummaryState === 'failed' && (
          <p role="alert" className="text-red-600 mb-4">{t('closure_summary_failed')}</p>
        )}
        {closureSummaryState === 'ready' && closureBlocked && (
          <p role="alert" className="text-red-600 mb-4">
            {t('closure_owned_groups_block', { groups: closureSummary.blocking_groups.join(', ') })}
          </p>
        )}
        {closureSummaryState === 'ready' && !closureBlocked && (
          <>
            <p className="text-gray-600 mb-2">{t('closure_summary_intro')}</p>
            <ul className="text-gray-800 mb-3 list-disc pl-5">
              <li>{t('closure_count_articles', { count: closureSummary.articles })}</li>
              <li>{t('closure_count_events', { count: closureSummary.events })}</li>
              <li>{t('closure_count_comments', { count: closureSummary.comments })}</li>
              <li>{t('closure_count_messages', { count: closureSummary.messages })}</li>
              <li>{t('closure_count_others', { count: closureSummary.others_items })}</li>
            </ul>
            <p className="text-gray-600 mb-3">{t('closure_images_note')}</p>
            <p className="text-gray-800 mb-3">
              {t('closure_way_back', { date: formatClosureDeadline(closureSummary.deletes_on, formattingLocale) })}
            </p>
            <p className="text-gray-800 mb-3">{t('closure_delete_now')}</p>
            <p className="text-gray-800 font-semibold mb-3">{t('closure_keep_account')}</p>
            <p className="text-gray-600 mb-6">{t('delete_modal_message')}</p>
          </>
        )}
        {closureError && (
          <p role="alert" className="text-red-600 mb-4">{closureError}</p>
        )}
        <div className="flex gap-4 justify-end">
          <StandardButton
            variant="utility"
            label={t('delete_button_cancel')}
            disabled={saving}
            onClick={() => setShowDeleteConfirm(false)}
          />
          <StandardButton
            variant="destructive"
            iconName="Trash2"
            label={saving ? t('delete_button_deleting') : t('delete_button_delete')}
            confirmBefore={false}
            disabled={saving || closureSummaryState !== 'ready' || closureBlocked}
            onClick={handleDeleteAccount}
          />
        </div>
      </Modal>

      {/* Confirmation Modal */}
      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('modal_default_title')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else setModal({ visible: false, message: '', onOk: null });
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
      />

      {/* Footer (uses `Footer.jsx`) */}
      <FooterContainer viewType="form" isMobile={isMobile}>
        <FooterButtonsForm
          onCancel={handleCancel}
          onCreateAndExit={handleSave}
          isEditMode={true}
        />
      </FooterContainer>

      {/* NEW - Profile Picture Cropper Modal */}
      <ImageCropper
        isOpen={showProfileCropper}
        onClose={() => setShowProfileCropper(false)}
        onSave={handleProfileImageSave}
        aspectMode="inline"
        bucket="hero-images"
        userId={userId}
      />

      {/* NEW - My Images Modal */}
      {showImagesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '2px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src="/images/icons/icon-images-white.svg" alt="Images" style={{ width: '32px', height: '32px' }} />
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {t('images_modal_title')}
                </h2>
              </div>
              <button
                onClick={() => setShowImagesModal(false)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  fontSize: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {loadingImages ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {t('images_loading')}
                </div>
              ) : userImages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <img src="/images/icons/icon-images-white.svg" alt="No images" style={{ width: '64px', height: '64px', margin: '0 auto 16px', opacity: 0.3 }} />
                  <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>{t('images_empty_title')}</p>
                  <p style={{ fontSize: '14px' }}>{t('images_empty_description')}</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  {userImages.map((image) => (
                    <div key={image.id} style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}>
                      {/* Image */}
                      <img
                        src={image.url}
                        alt={image.name}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />

                      {/* Info & Delete */}
                      <div style={{
                        padding: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {image.name}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: '#9ca3af'
                        }}>
                          {image.bucket === 'hero-images' ? t('images_type_hero') : t('images_type_inline')} • {image.size ? `${Math.round(image.size / 1024)}KB` : t('images_size_unknown')}
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm(t('images_delete_confirm'))) {
                              handleDeleteImage(image);
                            }
                          }}
                          style={{
                            marginTop: '4px',
                            padding: '6px 12px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {t('images_button_delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '2px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f9fafb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                {userImages.length} {userImages.length === 1 ? t('images_count_singular') : t('images_count_plural')} {t('images_stored_suffix')}
              </div>
              <button
                onClick={() => setShowImagesModal(false)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('images_button_close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
